import { 
  CpuChipIcon, 
  ChartBarIcon,
  BoltIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const models = [
  {
    name: 'GPT-4',
    status: 'Operational',
    latency: '850ms',
    uptime: '99.9%',
    load: 75,
    type: 'healthy',
  },
  {
    name: 'GPT-3.5 Turbo',
    status: 'High Load',
    latency: '450ms',
    uptime: '99.8%',
    load: 92,
    type: 'warning',
  },
  {
    name: 'DALL-E 3',
    status: 'Operational',
    latency: '2.8s',
    uptime: '99.7%',
    load: 45,
    type: 'healthy',
  },
];

const insights = [
  {
    title: 'Load Balancing Recommended',
    description: 'GPT-3.5 Turbo is experiencing high load. Consider distributing traffic.',
    type: 'warning',
    icon: BoltIcon,
  },
  {
    title: 'Cost Optimization Available',
    description: 'Switch non-critical tasks to GPT-3.5 Turbo to reduce costs by ~40%',
    type: 'info',
    icon: ChartBarIcon,
  },
];

export default function ModelPerformance() {
  return (
    <div className="glass-panel">
      <div className="px-8 py-6 border-b border-white/[0.05] flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <CpuChipIcon className="h-6 w-6 text-white/90" />
          <h2 className="text-lg font-medium text-white">Model Performance</h2>
        </div>
        <button className="btn-secondary text-sm">View Details</button>
      </div>

      <div className="p-8">
        <div className="grid grid-cols-3 gap-6">
          {models.map((model) => (
            <div key={model.name} className="glass-panel-dark">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${
                      model.type === 'healthy' 
                        ? 'bg-emerald-500/10 text-emerald-300'
                        : 'bg-amber-500/10 text-amber-300'
                    }`}>
                      {model.type === 'healthy' 
                        ? <CheckCircleIcon className="h-5 w-5" />
                        : <ExclamationTriangleIcon className="h-5 w-5" />
                      }
                    </div>
                    <div>
                      <div className="font-medium text-white">{model.name}</div>
                      <div className="text-sm text-white/50">{model.status}</div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  <div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-white/50">Load</span>
                      <span className={`font-medium ${
                        model.load > 90 
                          ? 'text-amber-300' 
                          : model.load > 75 
                            ? 'text-amber-400/80' 
                            : 'text-emerald-300'
                      }`}>
                        {model.load}%
                      </span>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-white/[0.05]">
                      <div 
                        className={`h-full rounded-full ${
                          model.load > 90 
                            ? 'bg-amber-500' 
                            : model.load > 75 
                              ? 'bg-amber-400/80' 
                              : 'bg-emerald-500'
                        }`}
                        style={{ width: `${model.load}%` }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-white/50">Latency</div>
                      <div className="text-sm font-medium text-white mt-0.5">
                        {model.latency}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-white/50">Uptime</div>
                      <div className="text-sm font-medium text-white mt-0.5">
                        {model.uptime}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 space-y-4">
          {insights.map((insight) => (
            <div 
              key={insight.title}
              className={`glass-panel-dark p-4 ${
                insight.type === 'warning' 
                  ? 'bg-amber-500/5 ring-1 ring-amber-500/10' 
                  : 'bg-blue-500/5 ring-1 ring-blue-500/10'
              }`}
            >
              <div className="flex items-center space-x-4">
                <div className={`p-2 rounded-lg ${
                  insight.type === 'warning'
                    ? 'bg-amber-500/10 text-amber-300'
                    : 'bg-blue-500/10 text-blue-300'
                }`}>
                  <insight.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-medium text-white">
                    {insight.title}
                  </div>
                  <div className="text-sm text-white/50 mt-0.5">
                    {insight.description}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
